'use client'
// Custom hook to simplify currency price display\
import { DEFAULT_CURRENCY } from '@partment/types'
import { useEffect, useState } from 'react'
import { useCurrency } from '../Providers/CurrencyContext'
import { displayPriceWithCurrency, normalizeCurrencyDisplay } from '../lib/common'
import { storage } from '../utils/CurrencySelector/ExchangeStorage'
// Custom hook to simplify currency price display

export const useCurrencyPrice = () => {
  const { currency, isHydrated, ratesLoaded } = useCurrency()
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const displaySsrSafePrice = (
    usdAmount: number,
    currencyCode: string,
    decimals?: number,
  ) => {
    let formatted = displayPriceWithCurrency(
      usdAmount,
      currencyCode,
      decimals,
      'en-US',
    )

    return normalizeCurrencyDisplay(formatted, currencyCode)
  }

  const displayPrice = (usdAmount: number | string, decimals?: number) => {
    const amount = Number(usdAmount)
    if (!mounted) {
      return displaySsrSafePrice(amount, DEFAULT_CURRENCY, decimals)
    }

    const activeCurrency = mounted ? currency : DEFAULT_CURRENCY

    if (activeCurrency === 'USD') {
      const usdDisplay = displayPriceWithCurrency(amount, 'USD', decimals)
      return normalizeCurrencyDisplay(usdDisplay, 'USD')
    }

    // For non-USD currencies, check if rates are available
    if (typeof window !== 'undefined') {
      const rates = storage.getRates()
      if (!rates || !rates[activeCurrency]) {
        // If rates not loaded yet, fall back to USD to avoid showing misleading prices
        // (e.g., showing E£100 when it should be E£4,700 after conversion)
        console.warn(
          `Exchange rates not available for ${activeCurrency}, showing USD`,
        )
        const fallback = displayPriceWithCurrency(amount, 'USD', decimals)
        return normalizeCurrencyDisplay(fallback, 'USD')
      }
    }

    let formatted = displayPriceWithCurrency(amount, activeCurrency, decimals)

    return normalizeCurrencyDisplay(formatted, activeCurrency)
  }

  const displayAlternatePrice = (
    usdAmount: number | string,
    decimals?: number,
  ) => {
    const amount = Number(usdAmount)
    const targetCurrency = currency === 'USD' ? 'EGP' : 'USD'

    if (!mounted || !isHydrated) {
      return displaySsrSafePrice(amount, DEFAULT_CURRENCY, decimals)
    }

    if (typeof window !== 'undefined') {
      const rates = storage.getRates()
      if (!rates || !rates[targetCurrency]) {
        console.warn(
          `Exchange rates not available for ${targetCurrency}, showing USD`,
        )
        const fallback = displayPriceWithCurrency(amount, 'USD', decimals)
        return normalizeCurrencyDisplay(fallback, 'USD')
      }
    }

    let altDisplay = displayPriceWithCurrency(
      amount,
      targetCurrency,
      decimals,
    )

    return normalizeCurrencyDisplay(altDisplay, targetCurrency)
  }

  return {
    currency,
    isHydrated,
    displayPrice,
    displayAlternatePrice,
  }
}
